import { IS, NO, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CompanyViewController } from "../CompanyView/CompanyViewController"
import {
    CompanyMembershipRequestViewController
} from "../CompanyView/CompanyAffiliation/CompanyAffiliationViewController"
import { CompanyAffiliationViewController } from "../CompanyView/CompanyAffiliation/CompanyAffiliationViewController"
import { RHCore } from "../Core/RHCore"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCalloutView } from "../Custom components/RH/RHCalloutView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurerMainViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { UserMainViewController } from "./UserMainViewController"


// How many days before we warn the user their join request may have been missed
const JOIN_REQUEST_STALE_THRESHOLD_DAYS = 7


export class NonAffiliatedMainViewController extends UIViewController {
    
    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "Tere tulemast!",
        hoverText: "",
        backgroundColor: UIColor.transparentColor,
        textAlignment: UITextView.textAlignment.center
    }).addedAsSubviewToView(this.view)
    
    readonly affiliationAlertView = new RHAlertBannerView().configuredWithObject({
        text: "⚠️ Sa ei ole seotud ühegi ettevõttega. Hanke korraldamiseks või pakkumuse esitamiseks " +
            "peab sinu konto olema seotud ettevõttega.",
        alertType: RHAlertBannerView.type.Warning,
        textAlignment: UITextView.textAlignment.center
    }).addedAsSubviewToView(this.view)

    readonly membershipInvitationCalloutView = new RHCalloutView(
        "info",
        "Ettevõtte liikmekutse",
        ""
    )
    
    readonly companyAffiliationButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Seo ennast ettevõttega"
        }
    }).addedAsSubviewToView(this.view)
    
    readonly bottomView = new UIView().addedAsSubviewToView(this.view)
    
    // Shown only when the user registered via a procurement invitation and the
    // invited company is not yet on the platform (Case 2). Cleared once acted on.
    readonly companySuggestionBannerView = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Info,
        textAlignment: UITextView.textAlignment.center
    })
    
    readonly registerSuggestedCompanyButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Registreeri ettevõte" }
    })
    
    // Registry code stored from the suggestion, used to pre-fill RegisterCompany
    _suggestedRegistryCode?: string
    _shouldOpenCompanyMembershipRequest = YES
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [this.companyAffiliationButton]
        if (this.registerSuggestedCompanyButton.superview === this.view) {
            items.push(this.registerSuggestedCompanyButton)
        }
        return items
    })
    
    
    constructor(view: UIView) {
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        
        // noinspection JSConstantReassignment
        this.companyAffiliationButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (RHCore.sharedInstance.companyAffiliationStore.affiliationLoadState === "failed") {
                this.view.loading = YES
                await RHCore.sharedInstance.refreshCurrentCompanyAffiliation()
                await this._updateContent()
                this.view.loading = NO
                this.view.setNeedsLayoutUpToRootView()
                return
            }
            let targetRoute = UIRoute.currentRoute
                .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
                .routeWithViewControllerComponent(
                    CompanyViewController,
                    {
                        [CompanyViewController.ParameterIdentifierName.tab]: "administration"
                    }
                )
            if (this._shouldOpenCompanyMembershipRequest) {
                targetRoute = targetRoute.routeWithViewControllerComponent(
                    CompanyMembershipRequestViewController,
                    {}
                )
            }
            targetRoute.apply()
        }
        
        // noinspection JSConstantReassignment
        this.registerSuggestedCompanyButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
                .routeWithViewControllerComponent(
                    CompanyViewController,
                    {}
                ).apply()
        }
        
    }
    
    
    static override readonly routeComponentName = "non_affiliated_main"
    static override readonly ParameterIdentifierName = {}
    
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
    }
    
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
    }
    
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        await this._updateContent()
        this.view.setNeedsLayout()
    }
    
    
    private async _updateContent() {
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        this.membershipInvitationCalloutView.removeFromSuperview()
        this.affiliationAlertView.addedAsSubviewToView(this.view)
        if (companyAffiliationStore.affiliationLoadState === "failed") {
            this.affiliationAlertView.configureWithObject({
                text: "Ettevõtte seoste laadimine ebaõnnestus. Kontrolli ühendust ja proovi uuesti.",
                alertType: RHAlertBannerView.type.Warning
            })
            this.companyAffiliationButton.titleLabel.text = "Proovi uuesti"
            return
        }

        this.companyAffiliationButton.titleLabel.text = "Seo ennast ettevõttega"
        this._shouldOpenCompanyMembershipRequest = YES
        const affiliation = await RHCore.sharedInstance.currentCompanyAffiliation
        const pendingRequest = affiliation?.pendingJoinRequest
        const pendingRegistration = affiliation?.pendingRegistrationRequest
        const suggestion = affiliation?.pendingCompanySuggestion
        const pendingInvitation = companyAffiliationStore.membershipWorkflows.find(workflow =>
            workflow.direction === "company_to_user" &&
            workflow.status === "pending"
        )
        
        // ── Company suggestion banner (Case 2 invitation path) ────────────────
        if (suggestion) {
            this._suggestedRegistryCode = suggestion.registryCode
            this.companySuggestionBannerView.configureWithObject({
                text: `✉️ Sulle saadeti kutse osaleda hankes. Sinu kutses nimetatud ettevõte „${suggestion.companyName}“ (registrikood: ${suggestion.registryCode}) ei ole veel platvormil registreeritud. Kas soovid registreerida selle ettevõtte?`
            })
            this.companySuggestionBannerView.addedAsSubviewToView(this.view)
            this.registerSuggestedCompanyButton.addedAsSubviewToView(this.view)
        }
        else {
            this._suggestedRegistryCode = undefined
            this.companySuggestionBannerView.removeFromSuperview()
            this.registerSuggestedCompanyButton.removeFromSuperview()
        }
        
        // ── Pending join request / no-company state ───────────────────────────
        if (IS(pendingInvitation)) {
            this.membershipInvitationCalloutView.bodyText = "Ettevõte „" + pendingInvitation.company.name +
                "“ on kutsunud sind ettevõtte liikmeks. Kutse saad vastu võtta või tagasi lükata " +
                "vaates „Minu ettevõtted“."
            this.affiliationAlertView.removeFromSuperview()
            this.membershipInvitationCalloutView.addedAsSubviewToView(this.view)
            this.companyAffiliationButton.titleLabel.text = "Vaata kutset"
            this._shouldOpenCompanyMembershipRequest = NO
        }
        else if (IS(pendingRequest)) {
            const daysSinceRequest = (Date.now() - new Date(pendingRequest.createdAt).getTime()) /
                (1000 * 60 * 60 * 24)
            const isStale = daysSinceRequest >= JOIN_REQUEST_STALE_THRESHOLD_DAYS
            let expiryText = ""
            if (pendingRequest.expiresAt) {
                expiryText = " (" +
                    CompanyAffiliationViewController.expiryCountdownText(pendingRequest.expiresAt) + ")"
            }

            if (isStale) {
                this.affiliationAlertView.configureWithObject({
                    text: "⏳ Sinu liitumistaotlus ettevõttele „" + pendingRequest.companyName +
                        "“ on ootel juba üle " + JOIN_REQUEST_STALE_THRESHOLD_DAYS +
                        " päeva" + expiryText + ". Tõenäoliselt on see tähelepanuta jäänud. " +
                        "Võid taotluse tühistada ja uuesti proovida.",
                    alertType: RHAlertBannerView.type.Warning
                })
            }
            else {
                this.affiliationAlertView.configureWithObject({
                    text: "⏳ Sinu liitumistaotlus ettevõttele „" + pendingRequest.companyName +
                        "“ on ootel" + expiryText + ". Ettevõtte peakasutaja peab selle kinnitama.",
                    alertType: RHAlertBannerView.type.Warning
                })
            }
            this.companyAffiliationButton.titleLabel.text = "Vaata staatust"
            this._shouldOpenCompanyMembershipRequest = NO
        }
        else if (IS(pendingRegistration)) {
            this.affiliationAlertView.configureWithObject({
                text: "⏳ Ettevõtte „" + pendingRegistration.companyName +
                    "“ registreerimine on ootel (" +
                    CompanyAffiliationViewController.expiryCountdownText(pendingRegistration.expiresAt) +
                    ") — kinnituskiri on saadetud ettevõtte ametlikule e-posti aadressile.",
                alertType: RHAlertBannerView.type.Warning
            })
            this.companyAffiliationButton.titleLabel.text = "Vaata staatust"
            this._shouldOpenCompanyMembershipRequest = NO
        }
        else {
            this.affiliationAlertView.configureWithObject({
                text: "⚠️ Sa ei ole seotud ühegi ettevõttega. Hanke korraldamiseks või pakkumuse esitamiseks " +
                    "peab sinu konto olema seotud ettevõttega.",
                alertType: RHAlertBannerView.type.Warning
            })
        }
    }
    
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
            .rectangleWithMaxWidth(1100, 0.5)
        
        bounds
            .rectangleByAddingY(RHSpacing.medium)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            
            .rectangleForNextRow(RHSpacing.small)
            .rectangleWithHeight(0)
            
            .IF(this.companySuggestionBannerView.superview == this.view)
                .rectangleForNextRow(padding, this.companySuggestionBannerView)
                .assignedAsFrameOfView(this.companySuggestionBannerView)
                .rectangleForNextRow(padding, RHProcurerMainViewTokens.actionButtonHeight)
                .rectangleWithWidth(RHProcurerMainViewTokens.actionButtonWidth + 50, 0.5)
                .assignedAsFrameOfView(this.registerSuggestedCompanyButton)
                .rectangleForNextRow(padding * 2)
                .rectangleWithHeight(0)
            .ENDIF()
            
            .IF(this.membershipInvitationCalloutView.superview === this.view)
                .rectangleForNextRow(padding)
                .rectangleWithMaxWidth(720, 0.5)
                .rectangleWithHeight(this.membershipInvitationCalloutView)
                .assignedAsFrameOfView(this.membershipInvitationCalloutView)
            .ELSE()
                .rectangleForNextRow(padding, this.affiliationAlertView)
                .assignedAsFrameOfView(this.affiliationAlertView)
            .ENDIF()
            
            .rectangleForNextRow(padding, RHProcurerMainViewTokens.actionButtonHeight)
            .rectangleWithWidth(RHProcurerMainViewTokens.actionButtonWidth + 50, 0.5)
            .assignedAsFrameOfView(this.companyAffiliationButton)
            
            .rectangleForNextRow(padding, this.bottomView)
            .assignedAsFrameOfView(this.bottomView)
    }
    
}
